/**
 * Commerce Showcase — v2.0
 *
 * Classes:
 *   FilterEngine      — live search + tag filter chips (no page reload)
 *   CommerceShowcase  — sticky tabs, scroll-spy, smooth-scroll, AJAX cart
 *
 * Zero dependencies. RTL-aware. Accessible.
 */

(() => {
  'use strict';

  const registry = new WeakMap();

  /* ════════════════════════════════════════════════════════════════
     FILTER ENGINE
  ════════════════════════════════════════════════════════════════ */

  class FilterEngine {
    #root;
    #searchInput;
    #clearBtn;
    #filterBar;
    #allCards;
    #activeFilters;
    #debounceTimer;

    static BADGE_TAGS = new Set(['popular', 'new', 'spicy', 'sale', 'featured']);

    constructor(root) {
      this.#root          = root;
      this.#searchInput   = root.querySelector('[data-cs-search]');
      this.#clearBtn      = root.querySelector('[data-cs-search-clear]');
      this.#filterBar     = root.querySelector('[data-cs-filter-bar]');
      this.#allCards      = [];
      this.#activeFilters = new Set();
      this.#debounceTimer = null;
    }

    init() {
      /* Collect all cards once — stable reference list */
      this.#allCards = Array.from(this.#root.querySelectorAll('.cs-card'));
      if (!this.#allCards.length) return;

      this.#buildChips();
      this.#bindSearch();
      this.#bindChips();
    }

    /* ── Build filter chip buttons from product tag data ─────── */

    #buildChips() {
      if (!this.#filterBar) return;

      const found = new Set();
      this.#allCards.forEach((card) => {
        (card.dataset.tags || '')
          .split(',')
          .forEach((t) => {
            const n = t.trim().toLowerCase();
            if (FilterEngine.BADGE_TAGS.has(n)) found.add(n);
          });
      });

      if (!found.size) {
        this.#filterBar.hidden = true;
        return;
      }

      const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

      this.#filterBar.innerHTML =
        `<button type="button" class="cs-chip is-active" data-cs-chip="all">All</button>` +
        [...found]
          .map((t) => `<button type="button" class="cs-chip" data-cs-chip="${t}">${capitalize(t)}</button>`)
          .join('');

      this.#filterBar.hidden = false;
    }

    /* ── Search input binding (debounced 200 ms) ─────────────── */

    #bindSearch() {
      if (!this.#searchInput) return;

      this.#searchInput.addEventListener('input', () => {
        clearTimeout(this.#debounceTimer);
        this.#debounceTimer = setTimeout(() => this.#apply(), 200);

        if (this.#clearBtn) {
          this.#clearBtn.hidden = !this.#searchInput.value;
        }
      });

      this.#clearBtn?.addEventListener('click', () => {
        this.#searchInput.value = '';
        if (this.#clearBtn) this.#clearBtn.hidden = true;
        this.#apply();
        this.#searchInput.focus();
      });
    }

    /* ── Chip click: toggle active filter ────────────────────── */

    #bindChips() {
      if (!this.#filterBar) return;

      this.#filterBar.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-cs-chip]');
        if (!chip) return;

        const tag = chip.dataset.csChip;

        if (tag === 'all') {
          this.#activeFilters.clear();
        } else {
          this.#activeFilters.has(tag)
            ? this.#activeFilters.delete(tag)
            : this.#activeFilters.add(tag);
        }

        /* Sync active state on all chips */
        this.#filterBar.querySelectorAll('[data-cs-chip]').forEach((c) => {
          const isAll    = c.dataset.csChip === 'all';
          const isActive = isAll
            ? this.#activeFilters.size === 0
            : this.#activeFilters.has(c.dataset.csChip);
          c.classList.toggle('is-active', isActive);
          c.setAttribute('aria-pressed', String(isActive));
        });

        this.#apply();
      });
    }

    /* ── Core filter logic ───────────────────────────────────── */

    #apply() {
      const query  = this.#searchInput?.value.trim().toLowerCase() ?? '';
      const hasQ   = query.length > 0;
      const hasF   = this.#activeFilters.size > 0;

      this.#allCards.forEach((card) => {
        let show = true;

        if (hasQ) {
          const title = (card.dataset.title ?? '').toLowerCase();
          const tags  = (card.dataset.tags  ?? '').toLowerCase();
          const type  = (card.dataset.type  ?? '').toLowerCase();
          show = title.includes(query) || tags.includes(query) || type.includes(query);
        }

        if (show && hasF) {
          const cardTags = (card.dataset.tags ?? '')
            .split(',')
            .map((t) => t.trim().toLowerCase());
          show = [...this.#activeFilters].some((f) => cardTags.includes(f));
        }

        card.hidden = !show;
      });

      /* Per-collection empty message */
      this.#root.querySelectorAll('[data-collection-handle]').forEach((sec) => {
        const anyVisible = Array.from(sec.querySelectorAll('.cs-card'))
          .some((c) => !c.hidden);

        let msg = sec.querySelector('[data-cs-filter-msg]');

        if (!anyVisible && (hasQ || hasF)) {
          if (!msg) {
            msg = document.createElement('p');
            msg.className           = 'cs-filter-msg';
            msg.dataset.csFilterMsg = '';
            sec.querySelector('.cs-grid')?.insertAdjacentElement('afterend', msg);
          }
          msg.textContent = 'No products match your search.';
          msg.hidden = false;
        } else if (msg) {
          msg.hidden = true;
        }
      });
    }
  }

  /* ════════════════════════════════════════════════════════════════
     COMMERCE SHOWCASE
  ════════════════════════════════════════════════════════════════ */

  class CommerceShowcase {
    #root;
    #sectionId;
    #mode;

    /* scroll-menu state */
    #tabs;
    #collectionEls;
    #tabsWrap;
    #tabsEl;
    #activeHandle;
    #visibleSet;
    #isScrolling;
    #scrollTimer;
    #spyObserver;
    #sentinelObserver;
    #tabsPlaceholder;   /* spacer used when tabs go position:fixed */
    #stickyResizeObs;

    /* sub-systems */
    #filter;

    constructor(root) {
      this.#root      = root;
      this.#sectionId = root.dataset.sectionId || root.id;
      this.#mode      = root.dataset.mode || 'scroll_menu';

      this.#tabs             = [];
      this.#collectionEls    = [];
      this.#tabsWrap         = null;
      this.#tabsEl           = null;
      this.#activeHandle     = null;
      this.#visibleSet       = new Set();
      this.#isScrolling      = false;
      this.#scrollTimer      = null;
      this.#spyObserver      = null;
      this.#sentinelObserver = null;
      this.#tabsPlaceholder  = null;
      this.#stickyResizeObs  = null;
      this.#filter           = null;
    }

    /* ── Public API ─────────────────────────────────────────────── */

    init() {
      this.#bindForms(this.#root);

      if (this.#mode === 'scroll_menu') {
        this.#initScrollMenu();
      }

      const { showSearch, showFilters } = this.#root.dataset;
      if (showSearch === 'true' || showFilters === 'true') {
        this.#filter = new FilterEngine(this.#root);
        this.#filter.init();
      }
    }

    destroy() {
      this.#spyObserver?.disconnect();
      this.#sentinelObserver?.disconnect();
      this.#stickyResizeObs?.disconnect();
      clearTimeout(this.#scrollTimer);
    }

    /* ════════════════════════════════════════════════════════════
       SCROLL MENU
    ════════════════════════════════════════════════════════════ */

    #initScrollMenu() {
      this.#tabs          = Array.from(this.#root.querySelectorAll('[data-cs-tab]'));
      this.#collectionEls = Array.from(this.#root.querySelectorAll('[data-collection-handle]'));
      this.#tabsWrap      = this.#root.querySelector('[data-cs-tabs-wrap]');
      this.#tabsEl        = this.#root.querySelector('[data-cs-tabs]');

      if (!this.#tabs.length || !this.#collectionEls.length) return;

      this.#bindTabs();
      this.#initScrollSpy();
      this.#initStickySentinel();

      /* Set first tab active on load */
      const first = this.#tabs[0]?.dataset.csTab;
      if (first) this.#setActiveTab(first, false);
    }

    /* ── Sticky tabs: CSS sticky + JS fixed fallback ───────────────
     *
     * CSS sticky works in most themes. However some Shopify themes
     * place overflow:hidden on an ancestor, which silently breaks it.
     *
     * Strategy:
     *  1. Let CSS sticky run (position:sticky).
     *  2. After sentinel leaves viewport, verify tabs actually stuck.
     *  3. If they didn't stick (CSS sticky is broken), switch to
     *     position:fixed via .is-js-sticky + a placeholder spacer.
     */

    #initStickySentinel() {
      const sentinel = this.#root.querySelector('[data-cs-sentinel]');
      if (!sentinel || !this.#tabsWrap) return;

      const isContained = this.#root.dataset.csContained === 'true';
      const stickyOff   = this.#cssVar('--cs-tabs-offset', 0);

      /* Placeholder fills the layout gap when tabs go position:fixed */
      this.#tabsPlaceholder = document.createElement('div');
      this.#tabsPlaceholder.className = 'cs-tabs-placeholder';
      this.#tabsPlaceholder.setAttribute('aria-hidden', 'true');
      this.#tabsWrap.insertAdjacentElement('afterend', this.#tabsPlaceholder);

      /* Keep placeholder height in sync if tabs height changes */
      this.#stickyResizeObs = new ResizeObserver(() => {
        if (this.#tabsWrap.classList.contains('is-js-sticky')) {
          this.#tabsPlaceholder.style.height =
            `${this.#tabsWrap.offsetHeight}px`;
        }
      });
      this.#stickyResizeObs.observe(this.#tabsWrap);

      this.#sentinelObserver = new IntersectionObserver(
        ([entry]) => {
          const shouldStick = !entry.isIntersecting;

          if (shouldStick) {
            if (!isContained) {
              /*
               * JS fixed fallback: only for normal (page-scroll) mode.
               * In contained mode CSS sticky always works because
               * .cs--contained is itself the scrolling ancestor.
               */
              const rect      = this.#tabsWrap.getBoundingClientRect();
              const cssSticky = Math.abs(rect.top - stickyOff) < 2;

              if (!cssSticky) {
                const h = this.#tabsWrap.offsetHeight;
                this.#tabsWrap.style.top = `${stickyOff}px`;
                this.#tabsPlaceholder.style.height = `${h}px`;
                this.#tabsPlaceholder.style.display = 'block';
                this.#tabsWrap.classList.add('is-js-sticky');
              }
            }

            this.#tabsWrap.classList.add('is-stuck');

          } else {
            this.#tabsWrap.classList.remove('is-stuck', 'is-js-sticky');
            this.#tabsWrap.style.top = '';
            if (!isContained) this.#tabsPlaceholder.style.display = 'none';
          }
        },
        {
          threshold  : 0,
          root       : isContained ? this.#root : null,
          rootMargin : (stickyOff > 0 && !isContained) ? `-${stickyOff}px 0px 0px 0px` : '0px',
        }
      );

      this.#sentinelObserver.observe(sentinel);
    }

    /* ── Bind tab click + keyboard ──────────────────────────────── */

    #bindTabs() {
      this.#tabs.forEach((tab, i) => {

        tab.addEventListener('click', () => {
          const h = tab.dataset.csTab;
          this.#setActiveTab(h, false);
          this.#scrollToSection(h);
        });

        tab.addEventListener('keydown', (e) => {
          const last = this.#tabs.length - 1;
          let next   = i;

          switch (e.key) {
            case 'ArrowRight': case 'ArrowDown':  next = i === last ? 0    : i + 1; break;
            case 'ArrowLeft':  case 'ArrowUp':    next = i === 0    ? last : i - 1; break;
            case 'Home':  next = 0;    break;
            case 'End':   next = last; break;
            default: return;
          }

          e.preventDefault();
          const target = this.#tabs[next];
          target.focus();
          this.#setActiveTab(target.dataset.csTab, false);
          this.#scrollToSection(target.dataset.csTab);
        });

      });
    }

    /* ── Activate a tab by handle ───────────────────────────────── */

    #setActiveTab(handle, scroll = true) {
      if (this.#activeHandle === handle && !scroll) return;
      this.#activeHandle = handle;

      this.#tabs.forEach((tab) => {
        const active = tab.dataset.csTab === handle;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('tabindex', active ? '0' : '-1');
        if (active) this.#scrollTabIntoView(tab);
      });
    }

    /* ── Keep active tab pill visible in horizontal scroll ──────── */

    #scrollTabIntoView(tab) {
      if (!this.#tabsEl) return;
      const tR = tab.getBoundingClientRect();
      const bR = this.#tabsEl.getBoundingClientRect();

      if (tR.left < bR.left + 16 || tR.right > bR.right - 16) {
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    /* ── Smooth-scroll to a collection section ─────────────────────
     *  Contained mode → scrolls this.#root (the section box).
     *  Normal mode    → scrolls window.
     */

    #scrollToSection(handle) {
      const target =
        this.#root.querySelector(`[data-collection-handle="${handle}"]`) ||
        document.getElementById(`${this.#sectionId}-${handle}`);

      if (!target) return;

      this.#isScrolling = true;

      const tabsH     = this.#tabsWrap?.offsetHeight ?? 64;
      const stickyOff = this.#cssVar('--cs-tabs-offset', 0);
      const offset    = tabsH + stickyOff + 12;

      if (this.#root.dataset.csContained === 'true') {
        /* Scroll the container, not the page */
        const rootRect   = this.#root.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const scrollTop  = this.#root.scrollTop + (targetRect.top - rootRect.top) - offset;
        this.#root.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      } else {
        const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      }

      clearTimeout(this.#scrollTimer);
      this.#scrollTimer = setTimeout(() => { this.#isScrolling = false; }, 900);
    }

    /* ── IntersectionObserver scroll-spy ────────────────────────────
     *  Contained mode → observe relative to this.#root (the scroll box).
     *  Normal mode    → observe relative to viewport (root: null).
     */

    #initScrollSpy() {
      const tabsH       = this.#tabsWrap?.offsetHeight ?? 56;
      const stickyOff   = this.#cssVar('--cs-tabs-offset', 0);
      const topMargin   = tabsH + stickyOff;
      const isContained = this.#root.dataset.csContained === 'true';

      this.#spyObserver = new IntersectionObserver(
        (entries) => {
          if (this.#isScrolling) return;

          entries.forEach((entry) => {
            const h = entry.target.dataset.collectionHandle;
            entry.isIntersecting
              ? this.#visibleSet.add(h)
              : this.#visibleSet.delete(h);
          });

          this.#updateActiveFromVisible();
        },
        {
          root       : isContained ? this.#root : null,
          rootMargin : `-${topMargin + 4}px 0px -40% 0px`,
          threshold  : 0,
        }
      );

      this.#collectionEls.forEach((el) => this.#spyObserver.observe(el));
    }

    /* Pick the topmost visible section in DOM order */
    #updateActiveFromVisible() {
      if (!this.#visibleSet.size) return;
      for (const el of this.#collectionEls) {
        if (this.#visibleSet.has(el.dataset.collectionHandle)) {
          this.#setActiveTab(el.dataset.collectionHandle, false);
          return;
        }
      }
    }

    /* ════════════════════════════════════════════════════════════
       AJAX CART
    ════════════════════════════════════════════════════════════ */

    #bindForms(scope) {
      scope.querySelectorAll('[data-cs-form]').forEach((form) => {
        // Delegate to the theme's ap-productform custom element; our handler
        // must not call preventDefault() on these forms or the cart drawer
        // will never receive the add event.
        if (form.getAttribute('is') === 'ap-productform') return;
        if (form.dataset.csBound) return;
        form.dataset.csBound = '1';
        form.addEventListener('submit', (e) => this.#handleSubmit(form, e));
      });
    }

    async #handleSubmit(form, event) {
      event.preventDefault();

      const btn = form.querySelector('[data-cs-add]');
      const fb  = form.querySelector('[data-cs-feedback]');
      if (!btn) return;

      const original = btn.textContent.trim();
      btn.disabled    = true;
      btn.textContent = btn.dataset.loadingLabel || '…';
      if (fb) fb.textContent = '';

      const variantId = form.querySelector('[name="id"]')?.value;
      const qty       = Number(form.querySelector('[name="quantity"]')?.value || 1);

      if (!variantId) {
        this.#restoreBtn(btn, original, fb, form.dataset.error || 'Error');
        return;
      }

      try {
        const root = window.Shopify?.routes?.root ?? '/';
        const res  = await fetch(`${root}cart/add.js`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify({ id: variantId, quantity: qty }),
        });

        if (!res.ok) throw new Error('cart/add failed');
        await res.json();

        if (fb) {
          fb.textContent = form.dataset.success || 'Added';
          setTimeout(() => { if (fb) fb.textContent = ''; }, 2400);
        }

        /* Let theme cart drawer / bubble know an item was added */
        document.dispatchEvent(
          new CustomEvent('commerce-showcase:item-added', {
            bubbles: true,
            detail:  { id: variantId, quantity: qty },
          })
        );

      } catch {
        if (fb) fb.textContent = form.dataset.error || 'Error';
      } finally {
        btn.disabled    = false;
        btn.textContent = original;
      }
    }

    #restoreBtn(btn, original, fb, msg) {
      btn.disabled    = false;
      btn.textContent = original;
      if (fb) fb.textContent = msg;
    }

    /* ── CSS variable helper ─────────────────────────────────────── */

    #cssVar(name, fallback = 0) {
      const raw = getComputedStyle(this.#root).getPropertyValue(name).trim();
      return raw ? (parseInt(raw, 10) || fallback) : fallback;
    }
  }

  /* ════════════════════════════════════════════════════════════════
     MOUNT / UNMOUNT
  ════════════════════════════════════════════════════════════════ */

  const getTargets = (scope) =>
    scope?.matches?.('[data-commerce-showcase]')
      ? [scope]
      : Array.from((scope ?? document).querySelectorAll('[data-commerce-showcase]'));

  const mount = (scope) => {
    getTargets(scope).forEach((el) => {
      if (registry.has(el)) return;
      const instance = new CommerceShowcase(el);
      registry.set(el, instance);
      instance.init();
    });
  };

  const unmount = (scope) => {
    getTargets(scope).forEach((el) => {
      registry.get(el)?.destroy();
      registry.delete(el);
    });
  };

  document.addEventListener('DOMContentLoaded', () => mount());
  document.addEventListener('shopify:section:load',   (e) => mount(e.target));
  document.addEventListener('shopify:section:unload', (e) => unmount(e.target));

})();
